import React, { useState, useEffect, useContext } from "react";
import { TouchableOpacity, StyleSheet, ScrollView, TextInput } from "react-native";
import { Text, View } from "../../../components/shared/Themed";
import {
  headers,
  MediumDeviceHeaders,
  SmallDeviceHeaders,
} from "./createUserHeaders";
import Modal from "../../../components/custom/Modal";
import CreateOktaBypassUserAccountForm from "./CreateOktaBypassUserAccountForm";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { NestedDataTable } from "../../../components/custom/DataTable/NestedDataTableWithPagination2";
import { useAppTheme } from "../../../hooks/useAppTheme";
import type { ThemeColors } from "../../../utils/constants/colors";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllUsers,
  searchAllUsers,
} from "../../../redux/dispatcher/usersDispatcher";
import Icon from "react-native-vector-icons/Feather";
import { FontAwesome } from "@expo/vector-icons";

export default function CreateUserSettingsScreen({ navigation }: any) {
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const dispatch = useDispatch<any>();

  const { colors, isDark } = useAppTheme();
  const styles = createStyles(colors, isDark);
  const [isRefresh, setRefresh] = useState(false);
  const [searchText, setSearchText] = useState("");
  const { currentCompany } = useSelector((state: any) => state.configurations);

  const { loading, users } = useSelector((state: any) => state.users);
  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      JSON.parse(JSON.stringify(headers))
    );
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const fetchUsers = () => {
    dispatch(getAllUsers(currentCompany._id || ""));
  };
  const { isLargeDevice, isMediumDevice, isSmallDevice } = useContext(
    DeviceOrientationContext
  );

  useEffect(() => {
    setRefresh(false);
    let isMounted = true;

    if (isMounted) {
      fetchUsers();

      setTableHeaders(
        JSON.parse(JSON.stringify(
          isLargeDevice
            ? headers
            : isMediumDevice
              ? MediumDeviceHeaders
              : SmallDeviceHeaders
        ))
      );
    }

    return () => {
      isMounted = false;
    };
  }, [isRefresh]);

  const handleSearch = (text: string) => {
    setSearchText(text);
  };

  let filteredUsers = users?.filter((user: any) => {
    if (!searchText || searchText.trim() === "") return true;
    const searchLower = searchText.toLowerCase();
    return (
      user.email?.toLowerCase().includes(searchLower) ||
      user.display_name?.toLowerCase().includes(searchLower) ||
      user.first_name?.toLowerCase().includes(searchLower) ||
      user.last_name?.toLowerCase().includes(searchLower) ||
      user.team?.toLowerCase().includes(searchLower) ||
      user.roles?.[0]?.role?.toLowerCase().includes(searchLower)
    );
  }) || [];

  if (sortColumn) {
    filteredUsers = [...filteredUsers].sort((a: any, b: any) => {
      let valA = a[sortColumn];
      let valB = b[sortColumn];

      if (sortColumn === "role") {
        valA = a.roles?.[0]?.role || "";
        valB = b.roles?.[0]?.role || "";
      }

      if (valA == null) valA = "";
      if (valB == null) valB = "";

      // String comparison
      if (typeof valA === "string" && typeof valB === "string") {
        valA = valA.toLowerCase();
        valB = valB.toLowerCase();
      }

      if (valA < valB) return sortDirection === "asc" ? -1 : 1;
      if (valA > valB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }

  const handleNestedSortColumn = (columnKey: string) => {
    let newDirection: "asc" | "desc" = "asc";
    if (sortColumn === columnKey && sortDirection === "asc") {
      newDirection = "desc";
    }

    setSortColumn(columnKey);
    setSortDirection(newDirection);

    const updatedHeaders = JSON.parse(JSON.stringify(tableHeaders));
    Object.keys(updatedHeaders).forEach((k) => {
      if (updatedHeaders[k].isSort) {
        updatedHeaders[k].sortDirection = "";
      }
    });
    if (updatedHeaders[columnKey]) {
      updatedHeaders[columnKey].sortDirection = newDirection;
    }
    setTableHeaders(updatedHeaders);
  };

  const navigateToOrder = (userId: string, mode: string) => {
    navigation.navigate("BypassUserDetails", { mode: mode, userId: userId });
  };

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={[
        styles.contentContainer,
        isSmallDevice && { paddingHorizontal: 14 },
      ]}
    >
      {/* Header */}
      <View style={[styles.pageHeader, isSmallDevice && { paddingHorizontal: 14, marginHorizontal: -14 }]}>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          {isSmallDevice && (
            <TouchableOpacity
              onPress={() => navigation.navigate("SettingsM")}
              style={styles.backButton}
            >
              <Icon name="arrow-left" size={18} color="#2563EB" />
            </TouchableOpacity>
          )}
          <View style={{ flex: 1, paddingRight: isSmallDevice ? 10 : 0 }}>
            <Text style={[styles.pageTitle, isSmallDevice && { fontSize: 20 }]} numberOfLines={1}>
              User Management
            </Text>
            <Text style={[styles.pageSubtitle, isSmallDevice && { fontSize: 12 }]} numberOfLines={1}>
              Manage team members and roles
            </Text>
          </View>
        </View>
      </View>


      {/* Toolbar */}
      <View style={[styles.toolbar, isSmallDevice && { flexDirection: "column", gap: 10 }]}>
        <View style={[styles.searchRow, isSmallDevice && { width: "100%" }]}>
          <View style={styles.searchContainer}>
            <Icon name="search" size={16} color="#9CA3AF" style={{ marginRight: 8 }} />
            <TextInput
              style={styles.searchInput}
              placeholder="Search users..."
              placeholderTextColor="#9CA3AF"
              value={searchText}
              onChangeText={handleSearch}
            />
          </View>
          <TouchableOpacity
            style={styles.refreshButton}
            onPress={() => {
              setRefresh(true);
              setSortColumn(null);
              setSortDirection("asc");
            }}
          >
            <Icon name="refresh-cw" size={15} color="#6B7280" />
          </TouchableOpacity>
        </View>
        <TouchableOpacity
          style={[styles.addButton, isSmallDevice && { alignSelf: "flex-start" }]}
          onPress={() =>
            navigation.navigate("CreateUser", { screen: "AddUserScreen" })
          }
        >
          <FontAwesome name="plus" size={12} color="#fff" style={{ marginRight: 6 }} />
          <Text style={styles.addButtonText}>Add User</Text>
        </TouchableOpacity>
      </View>

      {/* Table */}
      <View style={styles.tableContainer}>
        <NestedDataTable
          itemsPerPage={15}
          isNestedDataLoaded={loading}
          nestedHeader={tableHeaders}
          nestedRows={filteredUsers}
          key="rowKeyValue"
          userActions={navigateToOrder}
          userActionsKey="email"
          handleNestedSortColumn={handleNestedSortColumn}
        />
      </View>

      <Modal.ModalContainer
        modalVisible={modalVisible}
        setModalVisible={setModalVisible}
        childern={CreateOktaBypassUserAccountForm}
      />
    </ScrollView>
  );
}

function createStyles(colors: ThemeColors, isDark: boolean) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: isDark ? "#1F2937" : "#F9FAFB",
    },
    contentContainer: {
      paddingHorizontal: 24,
      paddingBottom: 48,
    },

    /* Header */
    pageHeader: {
      paddingTop: 20,
      paddingBottom: 16,
      marginBottom: 14,
      paddingHorizontal: 24,
      marginHorizontal: -24,
      backgroundColor: colors.surface,
      borderBottomWidth: 1,
      borderBottomColor: colors.borderSubtle,
    },
    backButton: {
      width: 38,
      height: 38,
      borderRadius: 12,
      backgroundColor: colors.surface,
      justifyContent: "center",
      alignItems: "center",
      marginRight: 12,
      borderWidth: 1,
      borderColor: colors.borderSubtle,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.06,
      shadowRadius: 3,
      elevation: 1,
    },
    pageTitle: {
      fontSize: 24,
      fontWeight: "700",
      color: colors.text,
    },
    pageSubtitle: {
      fontSize: 13,
      color: colors.textSecondary,
      marginTop: 2,
    },

  /* Toolbar */
  toolbar: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 14,
    gap: 10,
    backgroundColor: "transparent",
  },
  searchRow: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    gap: 8,
    backgroundColor: "transparent",
  },
    searchContainer: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.borderSubtle,
      borderRadius: 10,
      paddingHorizontal: 12,
      height: 40,
    },
    searchInput: {
      flex: 1,
      fontSize: 14,
      color: colors.text,
      height: 40,
      outlineStyle: "none",
    },
    refreshButton: {
      width: 40,
      height: 40,
      borderRadius: 10,
      backgroundColor: colors.surfaceSubtle,
      justifyContent: "center",
      alignItems: "center",
      borderWidth: 1,
      borderColor: colors.borderSubtle,
    },
  addButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#2563EB",
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 10,
    shadowColor: "#2563EB",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 2,
  },
    addButtonText: {
      color: "#fff",
      fontSize: 14,
      fontWeight: "600",
    },

    /* Table */
    tableContainer: {
      width: "100%",
      backgroundColor: colors.surface,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: colors.borderSubtle,
      overflow: "hidden",
    },
  });
}
